import { Label } from "@radix-ui/react-label";
import { CalendarIcon, Cross2Icon } from "@radix-ui/react-icons";
import { format } from "date-fns";
import { useEffect, useId, useMemo } from "react";
import {
    useFormContext,
    useController,
    type FieldValues,
    type Path,
} from "react-hook-form";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { useState } from "react";
import type { Matcher } from "react-day-picker";
import { useSchema } from "@/v2/hooks/SchemaContext";
import RequiredMark from "./RequiredMark";
import { ZodArray } from "zod";

interface ArDateFormFieldProps<T extends FieldValues> {
    name: Path<T>;
    label: string;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    // Choose between 'date' or 'string' for the form value type
    valueType?: "date" | "string";
    // Optional date constraints
    fromDate?: Date;
    toDate?: Date;
    disabledDays?: Date[];
    // Show clear button when date is selected
    showClearButton?: boolean;
}

export function ArDateFormField<T extends FieldValues>({
    label,
    name,
    placeholder = "Select date",
    className,
    disabled = false,
    valueType = "string",
    fromDate,
    toDate,
    disabledDays,
    showClearButton = false,
}: ArDateFormFieldProps<T>) {
    const {
        formState: { errors },
    } = useFormContext<T>();

    const [timeZone, setTimeZone] = useState<string | undefined>(undefined);

    useEffect(() => {
        setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    }, []);

    const {
        field: { value, onChange },
    } = useController({
        name,
    });

    const error = errors[name as keyof typeof errors];
    const id = useId();
    const schema = useSchema();
    const isRequired = useMemo(() => {
        const parts = name.split(".");
        let currentSchema: any = schema;

        for (const part of parts) {
            if (!currentSchema?.shape) return false;

            // Handle array index (numeric parts)
            if (/^\d+$/.test(part)) {
                if (currentSchema instanceof ZodArray) {
                    currentSchema = currentSchema.element;
                }
                continue;
            }

            currentSchema = currentSchema.shape[part];

            if (currentSchema instanceof ZodArray) {
                currentSchema = currentSchema.element;
            }
        }

        const testResult = currentSchema?.safeParse("");
        return !testResult?.success;
    }, [schema, name]);

    const [calendarOpen, setCalendarOpen] = useState(false);

    // Convert value to Date for display
    const dateValue = value
        ? valueType === "string"
            ? value
                ? new Date(value)
                : undefined
            : value
        : undefined;

    // Check if date is valid
    const isValidDate =
        dateValue instanceof Date && !isNaN(dateValue.getTime());

    const handleDateSelect = (date: Date | undefined) => {
        if (valueType === "string") {
            // Store as ISO string
            onChange(date ? date.toISOString() : undefined);
        } else {
            // Store as Date object
            onChange(date);
        }
        setCalendarOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
    };

    // Build the disabled matcher for the calendar
    const disabledMatcher: Matcher | Matcher[] | undefined = (() => {
        const matchers: Matcher[] = [];

        if (fromDate) {
            matchers.push({ before: fromDate });
        }

        if (toDate) {
            matchers.push({ after: toDate });
        }

        if (disabledDays && disabledDays.length > 0) {
            matchers.push(...disabledDays);
        }

        return matchers.length > 0 ? matchers : undefined;
    })();

    return (
        <div>
            <Label
                htmlFor={id}
                className={cn(
                    "text-sm font-medium",
                    error && "text-destructive",
                )}
            >
                {label}
                {isRequired && <RequiredMark />}
            </Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <div className="flex gap-2">
                    <PopoverTrigger asChild>
                        <Button
                            id={id}
                            variant="outline"
                            disabled={disabled}
                            className={cn(
                                "w-full justify-start text-left font-normal",
                                !isValidDate && "text-muted-foreground",
                                error &&
                                    "border-destructive focus-visible:ring-destructive",
                                className,
                            )}
                        >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {isValidDate ? (
                                format(dateValue, "PPP")
                            ) : (
                                <span>{placeholder}</span>
                            )}
                        </Button>
                    </PopoverTrigger>
                    {showClearButton && isValidDate && (
                        <Button onClick={handleClear} variant={"outline"}>
                            <Cross2Icon className="h-4 w-4 opacity-50 hover:opacity-100" />
                        </Button>
                    )}
                </div>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        fixedWeeks
                        mode="single"
                        selected={isValidDate ? dateValue : undefined}
                        onSelect={handleDateSelect}
                        disabled={disabledMatcher}
                        timeZone={timeZone}
                        autoFocus
                    />
                </PopoverContent>
            </Popover>
            {error && (
                <p id={`${id}-error`} className="mt-1 text-xs text-destructive">
                    {error.message as string}
                </p>
            )}
        </div>
    );
}
